import { v4 as uuidv4 } from 'uuid';

/**
 * Stable idempotency keys for room creation (ENG-4209).
 *
 * WHY sessionStorage instead of React state: the create-room dedup guard must
 * survive BOTH a network retry AND a component remount / full page reload within
 * the same tab session. Today's guard lives in React state that resets on remount
 * (ENG-4209 spec gap #5), so a lost-response retry mints a SECOND key and the
 * server creates a duplicate Studio. sessionStorage is the narrowest store that
 * (a) exists in BOTH the browser and the Electron renderer — so the shared
 * createRoom path does not fork per context (rules: architecture-principles,
 * ui-architecture) — and (b) is scoped to the tab session, so a brand-new session
 * (genuinely new user intent) naturally starts with no stored key.
 *
 * A "scope" is the stable identity of ONE create attempt (e.g. the link
 * requestId, or the bridge id for auto-link). Same scope => same key across
 * retries/remounts; a different scope, or an explicit rotate(), => a new key.
 *
 * Per rules (ai.knowledge/global): code-comments-required,
 * reference-rules-in-comments, architecture-principles, ui-architecture.
 */

// Namespaced so these entries never collide with other sessionStorage users and
// are recognizable in devtools. One entry per create attempt (keyed by scope).
const STORAGE_PREFIX = 'iris.createRoom.idempotencyKey.';

// In-memory fallback for the rare context with no Web Storage (Safari private
// mode, a locked-down Electron partition, or a test env without jsdom). Module
// scope means it still survives a React remount within the same JS context; it
// only loses the extra "survives a full reload" guarantee that sessionStorage
// adds. Never the primary path — sessionStorage is preferred whenever present.
const memoryFallback = new Map<string, string>();

const storageKeyFor = (scope: string): string => `${STORAGE_PREFIX}${scope}`;

// Resolve sessionStorage defensively: `typeof` never throws on an undeclared
// global, and access itself can throw in privacy modes — either way we degrade to
// the in-memory fallback rather than crash the create flow.
const getSessionStorage = (): Storage | undefined => {
  try {
    return typeof sessionStorage === 'undefined' ? undefined : sessionStorage;
  } catch {
    return undefined;
  }
};

const readStored = (scope: string): string | undefined => {
  const store = getSessionStorage();
  if (store !== undefined) {
    try {
      const value = store.getItem(storageKeyFor(scope));
      // Explicit null check (getItem returns string | null) — per coding rule,
      // no implicit truthiness.
      if (value !== null && value.length > 0) {
        return value;
      }
    } catch {
      // Fall through to the memory fallback below.
    }
  }
  return memoryFallback.get(scope);
};

const writeStored = (scope: string, key: string): void => {
  // Always populate the memory fallback so the value survives even if
  // sessionStorage.setItem throws (quota / privacy mode).
  memoryFallback.set(scope, key);
  const store = getSessionStorage();
  if (store !== undefined) {
    try {
      store.setItem(storageKeyFor(scope), key);
    } catch {
      // Best-effort persistence; the memory fallback already holds the value.
    }
  }
};

/**
 * Returns the stable idempotency key for `scope`, minting and persisting a fresh
 * crypto-quality UUID the first time. Calling it again for the SAME scope — on a
 * transport retry or after a remount/reload — returns the SAME key, so the server
 * dedups the create instead of provisioning a second Studio.
 *
 * @param scope Stable identity of ONE create attempt (link requestId, bridge id).
 * @returns The persisted idempotency key for this attempt.
 */
export const getOrCreateCreateRoomIdempotencyKey = (scope: string): string => {
  const existing = readStored(scope);
  if (existing !== undefined) {
    return existing;
  }
  // uuid is already a frontend dependency (see newts/router.ts) — reuse it for a
  // crypto-quality random seed rather than adding a new dependency.
  const minted = uuidv4();
  writeStored(scope, minted);
  return minted;
};

/**
 * Discards any persisted key for `scope` and mints a fresh one. Call this when the
 * user starts a GENUINELY NEW create for the same scope (not a retry of the same
 * attempt), so the new create is not deduped against the previous room.
 *
 * @param scope Stable identity of the create attempt to rotate.
 * @returns The freshly minted idempotency key.
 */
export const rotateCreateRoomIdempotencyKey = (scope: string): string => {
  const minted = uuidv4();
  writeStored(scope, minted);
  return minted;
};

/**
 * Removes the persisted key for `scope`. Optional cleanup once an attempt has
 * fully resolved; a subsequent create for the same scope will mint a new key.
 *
 * @param scope Stable identity of the create attempt to clear.
 */
export const clearCreateRoomIdempotencyKey = (scope: string): void => {
  memoryFallback.delete(scope);
  const store = getSessionStorage();
  if (store !== undefined) {
    try {
      store.removeItem(storageKeyFor(scope));
    } catch {
      // Best-effort removal; nothing else to do.
    }
  }
};
